/* FS mapping — moved out of pages/FsMapping.jsx in split step 6e (28-Oct-2026), unchanged. */
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Sparkles, Check, ChevronDown, X, Plus } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/dt";

/* Note picker — searchable, confirm-before-commit, with optional AI/hint suggestion chip. */
function NoteCombobox({ value, onCommit, notesByNo, suggestion, disabled, testId, onAddCustom }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(value || "");
  useEffect(() => { setPending(value || ""); }, [value]);
  const notes = Object.entries(notesByNo).sort(([a], [b]) => Number(a) - Number(b));
  const allItems = notes.flatMap(([, items]) => items);
  const displayLine = pending
    ? allItems.find((n) => n.segment_code === pending)
    : null;
  const dirty = pending !== (value || "");
  const commit = () => { onCommit(pending); setOpen(false); };
  const cancel = () => { setPending(value || ""); setOpen(false); };
  const acceptSuggestion = () => {
    if (!suggestion) return;
    setPending(suggestion.note_mapping);
    setOpen(true);
  };
  return (
    <div className="flex items-center gap-1.5">
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPending(value || ""); }}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled}
                  className={`h-8 w-64 justify-between font-normal ${dirty ? "border-emerald-500 ring-1 ring-emerald-200 dark:ring-emerald-900" : ""}`}
                  data-testid={`${testId}-trigger`}>
            {displayLine ? (
              <span className="flex items-center gap-1.5 truncate">
                <span className="font-mono text-[10px] text-muted-foreground">{displayLine.segment_code}</span>
                <span className="truncate" title={displayLine.title}>{displayLine.title}</span>
                {displayLine.custom && (
                  <span className="ms-1 rounded-md bg-fuchsia-100 px-1 py-0.5 text-[9px] font-semibold text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-200"
                        title={`Custom · created by ${displayLine.created_by_name || "?"} on ${fmtDate(displayLine.created_at)}`}>
                    <Sparkles className="me-0.5 inline h-2.5 w-2.5" />{t("fsMappingPage.note.customBadge")}
                  </span>
                )}
                {dirty && <span className="ms-1 rounded-md bg-emerald-100 px-1 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">{t("fsMappingPage.note.pending")}</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">{disabled ? t("fsMappingPage.note.pickFsFirst") : t("fsMappingPage.note.picker")}</span>
            )}
            <ChevronDown className="ms-1 h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[380px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t("fsMappingPage.note.searchPlaceholder")} data-testid={`${testId}-search`} />
            {/* Persistent "+ Add custom note line" pinned at the top —
                visible under any search filter (rendered outside CommandList).
                Available to every role at the firm. */}
            {onAddCustom && (
              <div className="border-b bg-emerald-50/60 px-2 py-1.5 dark:bg-emerald-950/40">
                <button type="button"
                        onClick={() => { setOpen(false); onAddCustom(); }}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-[12px] font-medium text-emerald-800 hover:bg-emerald-100 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
                        data-testid={`${testId}-add-custom`}>
                  <Plus className="h-3.5 w-3.5" />
                  <span>{t("fsMappingPage.note.addCustom")}</span>
                  <span className="ms-auto text-[10px] font-normal text-emerald-600/70 dark:text-emerald-300/60">
                    {t("fsMappingPage.note.addCustomTailored")}
                  </span>
                </button>
              </div>
            )}
            <CommandList className="max-h-[340px]">
              <CommandEmpty>{t("fsMappingPage.note.empty")}</CommandEmpty>
              {suggestion && (
                <CommandGroup heading={t("fsMappingPage.note.suggestionHeading")}>
                  <CommandItem value={`~sug ${suggestion.note_mapping} ${suggestion.title || ""}`}
                                onSelect={() => setPending(suggestion.note_mapping)}
                                data-testid={`${testId}-accept-suggestion`}>
                    <Sparkles className="me-2 h-3 w-3 text-emerald-600" />
                    <span className="font-mono text-[10px] me-2">{suggestion.note_mapping}</span>
                    <span className="flex-1">{suggestion.title || ""}</span>
                    <span className="ms-2 rounded-md bg-slate-200 px-1 py-0.5 text-[9px] uppercase text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                      {Math.round((suggestion.confidence || 0) * 100)}% · {suggestion.source}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup heading={t("fsMappingPage.note.clearHeading")}>
                <CommandItem value="~clear" onSelect={() => setPending("")}
                              data-testid={`${testId}-clear`}>
                  <X className="me-2 h-3 w-3" /> {t("fsMappingPage.note.clear")}
                </CommandItem>
              </CommandGroup>
              {notes.map(([no, items]) => (
                <CommandGroup key={no} heading={t("fsMappingPage.note.noteHeading").replace("{n}", no)}>
                  {items.map((n) => (
                    <CommandItem key={n.segment_code}
                                  value={`${n.segment_code} ${n.title}`}
                                  onSelect={() => setPending(n.segment_code)}
                                  data-testid={`${testId}-opt-${n.segment_code}`}>
                      <span className={pending === n.segment_code
                          ? "font-mono text-[10px] me-2 text-emerald-700 font-bold"
                          : "font-mono text-[10px] me-2 text-muted-foreground"}>
                        {n.segment_code}
                      </span>
                      <span className="flex-1">{n.title}</span>
                      {n.custom && (
                        <span className="ms-2 rounded-md bg-fuchsia-100 px-1 py-0.5 text-[9px] font-semibold text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-200"
                              title={`Custom · created by ${n.created_by_name || "?"} on ${fmtDate(n.created_at)}${n.description ? " — " + n.description : ""}`}>
                          {t("fsMappingPage.note.customBadge")}
                        </span>
                      )}
                      {pending === n.segment_code && <Check className="ms-2 h-3.5 w-3.5 text-emerald-600" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
            {dirty && (
              <div className="flex items-center justify-between gap-2 border-t bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
                <span className="text-[11px] text-muted-foreground">
                  {pending ? <>{t("fsMappingPage.note.confirmWrite")} <b className="font-mono">{pending}</b></> : t("fsMappingPage.note.confirmClear")}
                </span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" onClick={cancel} className="h-7"
                          data-testid={`${testId}-cancel`}>{t("fsMappingPage.note.cancel")}</Button>
                  <Button size="sm" onClick={commit}
                          className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`${testId}-apply`}>{t("fsMappingPage.note.apply")}</Button>
                </div>
              </div>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {suggestion && !value && (
        <button type="button" onClick={acceptSuggestion}
                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-800 hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                title={`Suggested by ${suggestion.source} (${Math.round((suggestion.confidence || 0) * 100)}%): ${suggestion.rationale || ""}`}
                data-testid={`${testId}-chip`}>
          <Sparkles className="h-2.5 w-2.5" />
          {suggestion.note_mapping} {t("fsMappingPage.note.chipReview")}
        </button>
      )}
    </div>
  );
}

export { NoteCombobox };
